/**
 * THE LIVE MATCH CLOCK, RELAYED FROM TMS - the rules a reading is judged by, written once.
 *
 * Authority: um-app/00-overview.md section 7.3 and UM-Q1 (ruled 2026-08-11) - TMS is the system
 * of record for match time and the clock reaches the phones RELAYED through the TD Mac mini;
 * um-app/02-capture.md section 2.7 (the stamp is a memory-jogger, always correctable);
 * control-centre/13-live-clock-relay.md (what the relay writes, and when); migration 063 (the
 * three columns it writes to). The Umpire Managers' first hands-on, 18 September 2026, is why
 * this exists at all: every quarter and minute was typed by hand, because no clock reached the
 * app.
 *
 * ---------------------------------------------------------------------------------------------
 * ONE CLOCK PER COMPARISON
 * ---------------------------------------------------------------------------------------------
 *
 * A reading's age is `serverNow - clockUpdatedAt`, and BOTH instants come from the server: the
 * relay stamps `clock_updated_at` on the database's clock (spec 13 section 4), and the API answers
 * `serverNow` from that same database. Nothing here takes a device's clock, and a caller that
 * passed `new Date()` as `serverNow` would be comparing a phone at a hockey venue - minutes out,
 * on a zone nobody set - with a database timestamp. The capture screen's tests pin this with a
 * phone clock hours adrift.
 *
 * ---------------------------------------------------------------------------------------------
 * WHAT A READING IS FOR
 * ---------------------------------------------------------------------------------------------
 *
 * A PRE-FILL, never a record. The relay lags TMS by up to about two minutes, TMS's minute is
 * whatever the technical table last pushed, and the manager may have seen the incident before
 * the minute turned. So a reading is offered in the two stamp fields, the fields stay editable,
 * and the note still goes out `minuteProvisional: true` exactly as a typed one does. What this
 * module decides is only whether a reading is good enough to OFFER.
 */
/**
 * How old a reading may be and still be offered: **three minutes**, by the server's clock.
 *
 * The relay reads every two minutes, so a healthy relay's newest reading is never much more than
 * two minutes old; the third minute is slack for one slow cycle. A reading older than this means
 * the relay has stopped or fallen behind, and the screen goes back to blank fields exactly as it
 * was before the relay existed.
 */
export const LIVE_CLOCK_FRESH_MS = 3 * 60 * 1000;
/** The largest minute migration 063's CHECK admits (`fixture_clock_minute_in_range`). */
export const LIVE_CLOCK_MAX_MINUTE = 120;
/** The largest seconds count migration 063's CHECK admits (`fixture_clock_seconds_in_range`). */
export const LIVE_CLOCK_MAX_SECONDS = 7200;
/**
 * The quarter a period names, or `null` for every period that is not a quarter.
 *
 * `Q1`..`Q4` map straight across. The two quarter breaks, half-time, a shoot-out, full time and
 * a match with no period yet name no quarter, and the answer is `null` rather than the nearest
 * quarter: a note written at half-time may be about the second quarter or about the break, and
 * the manager is the only one who knows which.
 */
export function quarterOfPeriod(period) {
    switch (period) {
        case "Q1":
            return 1;
        case "Q2":
            return 2;
        case "Q3":
            return 3;
        case "Q4":
            return 4;
        default:
            return null;
    }
}
/**
 * A match is in play while TMS reports it `live` - quarters and the breaks between them. Every
 * other status is a match not yet started (`warmup` and the appointment statuses) or finished
 * (`completed`, `official`), and a reading kept after full time (spec 13 section 5) must not
 * stamp a note written in the stand afterwards.
 */
export function matchIsInPlay(tmsStatus) {
    return tmsStatus === "live";
}
/**
 * How old a reading was when the server answered, in milliseconds - `serverNow` minus
 * `clockUpdatedAt`, both server instants. `null` when there is no reading or either instant is
 * unreadable. NEGATIVE when the reading is stamped after the server's own now, which a relay
 * keeping the contract cannot produce (see {@link liveClockPrefill}).
 */
export function liveClockAgeMs(serverNow, clockUpdatedAt) {
    if (clockUpdatedAt === null)
        return null;
    const now = Date.parse(serverNow);
    const read = Date.parse(clockUpdatedAt);
    if (Number.isNaN(now) || Number.isNaN(read))
        return null;
    return now - read;
}
/**
 * The pre-fill a reading supports, or `null` when it supports none.
 *
 * `null` - and so blank fields, exactly as before the relay - for each of:
 *
 *  * **not in play** - TMS does not report the match `live`;
 *  * **full time** - the period is `FT`, WHATEVER the status still says. TMS can go on reporting
 *    a match `live` for a while after the final whistle, and the relay writes the period it reads
 *    whether or not the status has moved yet; without this, every note written in that window
 *    would be stamped with the last minute of the match (the RUN 85 backend review). A shoot-out
 *    (`SO`) is still play and still offers the minute, with no quarter;
 *  * **absent** - no minute, or no instant it was read at, or a minute outside 063's range;
 *  * **stale** - read more than {@link LIVE_CLOCK_FRESH_MS} before the server's now;
 *  * **from the future** - stamped after the server's now. A relay keeping the contract stamps
 *    with the database's own `now()` and cannot produce one; a relay stamping a local wall-clock
 *    time without its zone can, nine hours ahead, and a check of staleness alone would then call
 *    that reading fresh all day. It is refused rather than trusted.
 */
export function liveClockPrefill(input) {
    if (!matchIsInPlay(input.tmsStatus))
        return null;
    if (input.period === "FT")
        return null;
    const minute = input.clockMinute;
    if (minute === null || !Number.isInteger(minute) || minute < 0 || minute > LIVE_CLOCK_MAX_MINUTE) {
        return null;
    }
    const ageMs = liveClockAgeMs(input.serverNow, input.clockUpdatedAt);
    if (ageMs === null || ageMs < 0 || ageMs > LIVE_CLOCK_FRESH_MS)
        return null;
    return { quarter: quarterOfPeriod(input.period), minute, ageMs };
}
/**
 * `Under 1 Min Ago`, `1 Min Ago`, `2 Min Ago`, `3 Min Ago` - the reading's age as the hint
 * says it.
 *
 * **Rounded UP to the whole minute**, on `undoWindowLabel`'s reasoning turned round: a claim
 * about how OLD something is must never make it sound fresher than it is. A reading 61 seconds
 * old reads `2 Min Ago`; 59 seconds reads `Under 1 Min Ago`.
 */
export function liveClockAgeLabel(ageMs) {
    if (ageMs < 60 * 1000)
        return "Under 1 Min Ago";
    return `${String(Math.ceil(ageMs / 60000))} Min Ago`;
}
/**
 * How long until {@link liveClockAgeLabel} changes, for a reading `ageMs` old: when a screen
 * showing the label must draw it again so that it never sounds fresher than the reading is (the
 * RUN 85 frontend review: a label drawn once and left there froze at its first value).
 *
 * The label turns at exactly one minute (`1 Min Ago`), and after that the moment the age passes
 * each whole minute (a reading 60.001 s old is `2 Min Ago`), so the answer is the time to the
 * first instant of the next label - never less than a millisecond.
 */
export function liveClockAgeLabelChangesInMs(ageMs) {
    const minute = 60 * 1000;
    if (ageMs < minute)
        return minute - ageMs;
    const shown = Math.ceil(ageMs / minute);
    return Math.max(1, shown * minute - ageMs + 1);
}
//# sourceMappingURL=live-clock.js.map